<script setup lang="ts">
// icons
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import dayjs from '@/utils/day';
import { RBN_CAMPSITE_DETAIL } from '@/utils/constants';

/********************
 * PROPS & EMITS    *
 ********************/
export interface CiAvailabilityInfoProps {
  availability: object;
  campsite?: any;
  primary?: boolean;
  snowplowContext: object;
}
const props = withDefaults(defineProps<CiAvailabilityInfoProps>(), {
  campsite: () => {},
  primary: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
const appStore = useAppStore();
const backendStore = useBackendStore();
const localePath = useLocalePath();
const route = useRoute();
const { $ngettext, interpolate, $gettext } = useGettext();
const { locale } = useI18n();
const { mapRating } = useRating();
const { trackAdClickAndFollow: DTrackAdClickAndFollow, trackAddToCart } = useTracking();
const { getContextForCP } = useECommerceContext();
const runtimeConfig = useRuntimeConfig();

/********************
 * REFS & VARS       *
 ********************/
const showDescription = ref(false);

const campsiteSlug = computed(() => {
  return props.campsite?.slug || '';
});

const hasFanclubDiscount = computed(() => {
  return (
    availabilityHasDiscount(props.availability, DISCOUNT_TYPE_FANCLUB)
    || availabilityHasDiscount({ choices: [props.availability] }, DISCOUNT_TYPE_FANCLUB)
  );
});

const accoType = computed(() => {
  if (props.availability.acco_type) {
    return props.availability.acco_type;
  }

  if (props.availability.pitch) {
    return 'pitch';
  }
  if (props.availability.acco) {
    return 'acco';
  }
  return '';
});

const bookingUrl = computed(() => {
  return `${backendStore.url}/external-link/?lang=${locale.value}&slug=${encodeURIComponent(
    campsiteSlug.value,
  )}&url=${encodeURIComponent(funnelUrl.value)}&link_identifier=campsite-detail-availability-funnel`;
});

const funnelUrl = computed(() => {
  let url = props.availability.funnelUrl;
  if (
    url.match(/^https:\/\/checkout\.camping\.info/)
    || url.match(/^https:\/\/[a-z0-9_-]+-jvbf-frontend\.test\.acsicamping\.info/)
    || url.match(/^https:\/\/checkout\.acsicamping\.info/)
  ) {
    const translated = $ngettext('%{count} rating', '%{count} ratings', props.campsite.rating_guest.rating_count);
    const numRatingsLabel = interpolate(translated, {
      count: props.campsite.rating_guest.rating_count,
    });

    const additionalData = {
      campsiteData: {
        camping_id: props.campsite.camping_id,
        language: locale.value,
        url: `${runtimeConfig.public.baseUrl.replace(/\/$/, '')}${localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: props.campsite.slug } })}`,
        rating: {
          count: {
            value: props.campsite.rating_guest.rating_count,
            label: numRatingsLabel,
          },
          value: props.campsite.rating_campsite.rating_avg_overall,
          label: mapRating(props.campsite.rating_campsite.rating_avg_overall),
        },
      },
    };

    const encryptedData = encrypt(additionalData);
    if (encryptedData) {
      url = `${url}&init=${encryptedData}`;
    }
  }
  return url;
});

const hasDetails = computed(() => {
  return (
    props.availability.description
    || props.availability.meta?.includedPersons
    || props.availability.meta?.maximumPersons
    || props.availability.meta?.minimumPersons
  );
});

const i18nShowDetails = computed(() => {
  return showDescription.value ? $gettext('hide details') : $gettext('show details');
});

const includedOptions = computed(() => {
  return props.availability.meta?.includedExtras?.filter(extra => !extra.isPerson);
});

/********************
 * INITIALIZATION    *
 ********************/
if ((campsiteSlug.value === '' || !campsiteSlug.value) && !props.availability.funnelUrl) {
  console.warn('Snippet has no campsite slug and no funnel url!');
}

/********************
 * FUNCTIONS         *
 ********************/
function getCurrency(data) {
  if (!data.currency) {
    return 'EUR';
  }
  if (typeof data.currency === 'object') {
    return data.currency.code || 'EUR';
  }
  return data.currency || 'EUR';
}

function getFormattedDate(date) {
  if (!date) {
    return;
  }
  return dayjs(date).format('DD.MM.YY');
}

function getDayForDate(date, format) {
  if (!date) {
    return;
  }
  return dayjs(date).format(format);
}

function getDetailQueryParams({ type, from, until, context } = { type: null, from: null, until: null, context: null }) {
  const query = { ...route.query };
  if (!query) {
    return {};
  }

  delete query.only_availabilities;
  delete query.offset;
  delete query.q;

  if (!type) {
    return query;
  } else {
    query.acco_type = type;
  }

  if (from) {
    query.from = from;
  }

  if (until) {
    query.until = until;
  }

  if (context) {
    query.context = context;
  }

  return { ...query };
}

function onBookNowClicked(event) {
  gtagReportConversion('book_now_click');
  trackAdClickAndFollow(event);

  const price = props.availability.price < props.availability.basePrice ? props.availability.price : props.availability.basePrice;
  trackAddToCart({
    products: [
      {
        id: props.campsite.camping_id.toString(),
        name: props.campsite.meta?.name,
        category: 'campsite',
        price: price,
        currency: 'EUR',
      },
    ],
    total_value: price,
    currency: 'EUR',
    context: getContextForCP(toRaw(props.campsite)),
  });
}

function trackAdClickAndFollow(event) {
  DTrackAdClickAndFollow(event, {
    bannerId: props.snowplowContext.bannerId || undefined,
    context: props.snowplowContext.contexts,
    impressionId: props.snowplowContext.impressionId,
    zoneId: props.snowplowContext.zone,
  });
}

function clickAvailability(event) {
  gtagReportConversion('choose_availability_click');
  appStore.clickContext = 'availability';
  trackAdClickAndFollow(event);
}
</script>

<template>
  <div class="mb-8 grid grid-cols-4 last:mb-0 md:mb-4 md:last:mb-0">
    <div
      v-if="hasFanclubDiscount || props.availability.type === 'special-offer'"
      class="col-span-4 grid grid-cols-12 md:col-span-3"
    >
      <div class="col-span-11 col-start-2">
        <div class="mx-2">
          <img
            v-if="hasFanclubDiscount"
            class="inline-block py-[3px]"
            src="~/assets/svg/fanclub-badge-with-logo.svg"
            alt="Fanclub"
            width="70"
            height="22"
          />
          <span
            v-if="props.availability.type === 'special-offer'"
            class="ml-1 inline-block rounded-[10rem] bg-danger px-[7px] py-[3px] text-xs font-medium leading-3 text-white"
          >{{ $gettext('special offer') }}</span>
        </div>
      </div>
    </div>

    <div class="col-span-4 mb-2 grid grid-cols-12 md:col-span-3 md:mb-0">
      <div class="col-span-1">
        <div class="flex h-full items-start justify-center">
          <img
            v-if="accoType === 'pitch'"
            src="~/assets/svg/icon-pitch.svg"
            width="26"
            alt="icon pitch"
            loading="lazy"
          />
          <img
            v-else
            src="~/assets/svg/icon-cabin.svg"
            width="26"
            alt="icon cabin"
            loading="lazy"
          />
        </div>
      </div>

      <div class="col-span-11">
        <div class="grid grid-cols-6 md:gap-x-[30px]">
          <div class="col-span-6 md:col-span-4">
            <div class="mx-2 flex h-full flex-col">
              <div class="flex flex-row flex-wrap items-end gap-1 text-sm">
                <span class="font-medium">{{ getFormattedDate(props.availability.start_date) }}</span>
                <span class="text-small-book text-gray">{{
                  getDayForDate(props.availability.start_date, 'dd')
                }}</span>
                -
                <span class="font-medium">{{ getFormattedDate(props.availability.end_date) }}</span>
                <span class="text-small-book pr-2 text-gray">{{
                  getDayForDate(props.availability.end_date, 'dd')
                }}</span>
                <span class="text-small-book whitespace-nowrap text-gray">
                  ({{
                    $ngettext('%{ count } night', '%{ count } nights', props.availability.duration, {
                      count: props.availability.duration,
                    })
                  }})
                </span>
              </div>

              <template v-if="props.availability.stock && props.availability.stock > 0">
                <template v-if="accoType === 'pitch'">
                  <div
                    v-if="props.availability.stock <= 5"
                    class="text-xs text-danger"
                  >
                    {{
                      $ngettext(
                        'Only %{stock} pitch available',
                        'Only %{stock} pitches available',
                        props.availability.stock,
                        { stock: props.availability.stock },
                      )
                    }}
                  </div>
                  <div
                    v-else
                    class="text-xs text-gray"
                  >
                    {{ $gettext('%{stock} pitches available', { stock: props.availability.stock }) }}
                  </div>
                </template>
                <template v-else>
                  <div
                    v-if="props.availability.stock <= 5"
                    class="text-xs text-danger"
                  >
                    {{
                      $ngettext(
                        'Only %{stock} rental accommodation available',
                        'Only %{stock} rental accommodations available',
                        props.availability.stock,
                        { stock: props.availability.stock },
                      )
                    }}
                  </div>
                  <div
                    v-else
                    class="text-xs text-gray"
                  >
                    {{ $gettext('%{stock} rental accommodations available', { stock: props.availability.stock }) }}
                  </div>
                </template>
              </template>

              <div
                v-if="props.availability.label"
                class="text-xs"
              >
                {{ props.availability.label }}
              </div>

              <div
                v-if="hasDetails"
                class="text-xs"
              >
                <template v-if="showDescription">
                  <div v-if="props.availability.description" class="availability-description my-2" v-html="props.availability.description" />
                  <template v-if="includedOptions?.length">
                    {{ $gettext('Included extras') }}: <span v-for="extra in includedOptions" :key="extra.label" class="mr-1 after:content-[','] last-of-type:mr-0 last-of-type:after:content-none">
                      <span v-if="extra.quantity > 1">{{ extra.quantity }}x </span>
                      {{ extra.label }}
                    </span>
                  </template>
                  <div v-if="props.availability.meta?.minimumPersons">
                    <span>{{ $gettext('Min persons') }}</span>: {{ props.availability.meta.minimumPersons }}
                  </div>
                  <div v-if="props.availability.meta?.maximumPersons">
                    <span>{{ $gettext('Max persons') }}</span>: {{ props.availability.meta.maximumPersons }}
                  </div>
                  <div v-if="props.availability.meta?.includedPersons">
                    <span>{{ $gettext('Included persons') }}</span>: {{ props.availability.meta.includedPersons }}
                  </div>
                </template>
                <div
                  class="flex cursor-pointer flex-row items-center text-primary"
                  @click="showDescription = !showDescription"
                >
                  <span>{{ i18nShowDetails }}</span>
                  <CiAwesomeIcon
                    class="ml-1 fill-primary"
                    ratio="0.5"
                    :icon="showDescription ? faChevronUp : faChevronDown"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="props.availability.price"
            class="col-span-6 md:col-span-2"
          >
            <div class="m-2 flex flex-row flex-wrap items-end md:m-0 md:mr-6 md:flex-col">
              <span
                class="text-input-book mr-2 whitespace-nowrap md:mr-0"
                :class="{ 'text-danger': props.availability.price < props.availability.basePrice }"
              >
                <span>{{ $pgettext('Campsite Tile availability price', 'from') }}</span>
                {{
                  new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: getCurrency(props.availability),
                  }).format(props.availability.price)
                }}</span>
              <span
                v-if="props.availability.price < props.availability.basePrice"
                class="mr-2 whitespace-nowrap text-xs line-through md:mr-0"
              >
                {{
                  new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: getCurrency(props.availability),
                  }).format(props.availability.basePrice)
                }}</span>
              <span class="text-small-book hidden text-gray md:inline">{{
                $ngettext('%{ count } night', '%{ count } nights', props.availability.duration, {
                  count: props.availability.duration,
                })
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-span-4 md:col-span-1">
      <CiButton
        v-if="props.availability.funnelUrl"
        tag="NuxtLink"
        class="w-full"
        :variant="props.primary ? 'primary' : 'secondary'"
        :href="bookingUrl"
        target="_blank"
        rel="noopener"
        @click="onBookNowClicked"
      >
        {{ $gettext('Proceed to booking') }}
        <CiAwesomeIcon
          :icon="faChevronRight"
          class="ml-2"
          ratio="0.6"
        />
      </CiButton>
      <CiButton
        v-else
        tag="NuxtLink"
        class="w-full"
        :variant="props.primary ? 'primary' : 'secondary'"
        :href="
          localePath({
            name: RBN_CAMPSITE_DETAIL,
            params: { identifier: campsiteSlug },
            query: getDetailQueryParams({
              from: props.availability.start_date,
              type: props.availability.acco_type,
              until: props.availability.end_date,
            }),
            hash: '#availabilities',
          })
        "
        @click.capture="clickAvailability"
      >
        {{ $pgettext('Campsite Tile choose availability', 'View offer') }}
        <CiAwesomeIcon
          class="ml-2"
          :icon="faChevronRight"
          ratio="0.6"
        />
      </CiButton>
    </div>
  </div>
</template>

<style lang="postcss">
.availability-description p {
  @apply mb-[0.2rem];
}
.availability-description ul {
  @apply mb-0 pl-4;
}
</style>
